<script setup>
import Brain from '/app/components/NBrain.vue';
import NumberFlow from '@number-flow/vue';
import { useStorage, toReactive, createReusableTemplate } from '@vueuse/core';
import { vElementVisibility } from '@vueuse/components';
import { useAuth } from '/app/composables/auth';
import { computed } from 'vue';
import { cn } from '/app/utils/helpers';

const props = defineProps({
    title: {
        type: String,
        required: true
    },
    level: {
        type: [String, Number],
        default: 0
    },
    position: {
        type: [String, Number],
        default: 0
    },
    score: {
        type: [String, Number],
        default: 0
    },
    startLevelScore: {
        type: [String, Number],
        default: 0
    },
    nextLevelScore: {
        type: [String, Number],
        default: 0
    },
    role: {
        type: String,
        default: 'overall'
    },
    tiny: {
        type: Boolean,
        default: false,
    }
});

const { auth: $auth } = useAuth();
const cached = toReactive(useStorage(`gamedash:nerve-score:${props.role}`, {
    score: 0,
    level: 0,
    levelProgress: 0,
}));

// How far the score has moved from this level's starting threshold toward the
// next level's, matching the meter on the score-stats brain level board.
const progressToNextLevel = computed(() => {
    const start = Number(props.startLevelScore) || 0;
    const next = Number(props.nextLevelScore) || 0;
    const score = Number(props.score) || 0;
    const span = next - start;

    if (span <= 0) {
        return Number(props.level) >= 100 ? 100 : 0;
    }

    return Math.max(0, Math.min(100, Math.round(((score - start) / span) * 100)));
});

const onScoreVisibility = (visible) => {
    if (visible) {
        setTimeout(() => {
            cached.score = Number(props.score) || 0;
            cached.level = Number(props.level) || 0;
            cached.levelProgress = progressToNextLevel.value;
        }, 1000);
    }
}

const [DefineBrainProgress, BrainProgress] = createReusableTemplate()
const [DefineBrainLevel, BrainLevel] = createReusableTemplate()
</script>

<template>
    <RouterLink v-if="$auth.authenticated"
        :to="{ name: 'dashboard.my-progress.score-stats', params: { platformSlug: $auth.user.screen_name || 'me' }, query: { tab: 'nerve-score' } }"
        data-heap-context="gamedash"
        data-heap-target="nerve-score"
        class="nerve-score" :class="cn('flex', `is-${role}`, !tiny && 'gap-6')">
        <DefineBrainProgress>
            <div class="nerve-score__level">
                <span class="nerve-score__level-title">You're at <b>Level
                        <NumberFlow :value="cached.level" :duration="4" :continuous="true" />
                    </b></span>
                <div v-tooltip="`Game Dash ${cached.levelProgress}% Meter to next level`" class="nerve-score__bar-wrapper">
                    <div class="nerve-score__bar ease-in-out transition-[width] duration-[3s]"
                        :style="{ width: `${cached.levelProgress}%` }"></div>
                </div>
            </div>
        </DefineBrainProgress>

        <DefineBrainLevel>
            <div :class="cn('w-[9rem] h-[9rem]', tiny && 'w-[4rem] h-[4rem]')">
                <Brain v-tooltip="`Level ${cached.level} / 100`" :level="cached.level" simple />
            </div>
        </DefineBrainLevel>

        <div v-element-visibility="onScoreVisibility"
            :class="cn(tiny && 'flex gap-2 items-end justify-end', !tiny && 'grid gap-2 flex-1')">
            <div>
                <h3 class="nerve-score__title">{{ title }}</h3>
                <div v-tooltip="{ html: true, content: `Brain Level: ${level}<br>Rank position: ${position}` }"
                    class="nerve-score__score" :class="{ tiny: 'text-lg' }">
                    <NumberFlow :value="cached.score" willChange continuous :transformTiming="{ duration: 750 }" />
                </div>
            </div>

            <BrainLevel v-if="tiny" />
            <BrainProgress v-else />
        </div>

        <BrainLevel v-if="!tiny" />
        <BrainProgress v-else />

    </RouterLink>
</template>
